import { Map, List } from 'immutable';
import { safeStringify } from './sortUtils';

/**
 * Utility function to normalize string for case-insensitive comparison
 * Updated to preserve camelCase and PascalCase
 * @param {any} str - String to normalize
 * @returns {string} - Normalized string
 */
export const normalizeForSearch = (str: any): string => {
    if (typeof str !== 'string') return String(str).toLowerCase();

    // Simple lowercase for exact matching of camelCase/PascalCase
    return str.toLowerCase();
};

/**
 * Enhanced check for determining if a value might contain a search term
 * @param {any} value - Value to check
 * @param {string} searchTerm - Search term to look for
 * @returns {boolean} - Whether the value might contain the search term
 */
export const valueMightContain = (value: any, searchTerm: string): boolean => {
    if (!searchTerm) return false;

    const normalizedSearch = normalizeForSearch(searchTerm);

    // Handle primitive values directly
    if (typeof value !== 'object' || value === null) {
        return normalizeForSearch(value).includes(normalizedSearch);
    }

    // Handle Immutable objects
    if (Map.isMap(value) || List.isList(value)) {
        // Basic check for immutable collections
        try {
            if (Map.isMap(value)) {
                // Check keys and values for potential matches
                return value.some((v: any, k: any) => {
                    // Check if key contains search term
                    const keyMatch =
                        normalizeForSearch(k).includes(normalizedSearch);

                    // For values, try both direct match and recursive check
                    let valueMatch = false;
                    try {
                        if (typeof v !== 'object' || v === null) {
                            valueMatch =
                                normalizeForSearch(v).includes(
                                    normalizedSearch,
                                );
                        } else if (
                            Map.isMap(v) ||
                            List.isList(v) ||
                            typeof v === 'object'
                        ) {
                            // Shallow check in nested objects/maps
                            valueMatch = normalizeForSearch(
                                safeStringify(v),
                            ).includes(normalizedSearch);
                        }
                    } catch (e) {
                        // Ignore stringification errors
                    }

                    return keyMatch || valueMatch;
                });
            } else {
                // For lists, check more items if we're looking for IDs
                const sampleSize = normalizedSearch.includes('-') ? 50 : 10;
                const sample = value.take(sampleSize).toArray();

                return sample.some((item: any) => {
                    if (typeof item === 'string') {
                        return normalizeForSearch(item).includes(
                            normalizedSearch,
                        );
                    } else if (typeof item === 'object' && item !== null) {
                        try {
                            return normalizeForSearch(
                                safeStringify(item),
                            ).includes(normalizedSearch);
                        } catch (e) {
                            return false;
                        }
                    }
                    return false;
                });
            }
        } catch (e) {
            return false;
        }
    }

    // For regular objects, use safe stringify
    try {
        return normalizeForSearch(safeStringify(value)).includes(
            normalizedSearch,
        );
    } catch (e) {
        return false;
    }
};

/**
 * Function to find paths to matching values with depth limiting and circular reference protection
 * @param {any} obj - Object to search in
 * @param {string} searchTerm - Term to search for
 * @param {string} currentPath - Current path in the object
 * @param {number} maxDepth - Maximum depth to search
 * @returns {Array<string>} - Array of paths that match the search term
 */
export const findPathsToMatchingValues = (
    obj: any,
    searchTerm: string,
    currentPath = '',
    maxDepth = 10,
): Array<string> => {
    if (!obj || !searchTerm) return [];

    const paths: string[] = [];
    const normalizedSearch = normalizeForSearch(searchTerm);
    const seen = new WeakSet(); // To avoid circular references

    // Helper to process an object or immutable structure
    const processObject = (value: any, path: string, depth = 0) => {
        // Limit recursion depth to avoid stack overflow
        if (depth > maxDepth) return;

        // Skip if we've seen this object before (circular reference)
        if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) return;
            seen.add(value);
        }

        // Check primitive values directly
        if (typeof value !== 'object' || value === null) {
            if (normalizeForSearch(value).includes(normalizedSearch)) {
                paths.push(path);
            }
            return;
        }

        // Handle Immutable Maps - with optimization for large maps
        if (Map.isMap(value)) {
            // For large maps, limit processing to improve performance
            const isLargeMap = value.size > 100;
            let processed = 0;

            value.forEach((v: any, k: any) => {
                // Skip after processing too many items in large maps
                if (isLargeMap && processed > 50) return;

                const newPath = path ? `${path}.${k}` : k;
                if (normalizeForSearch(k).includes(normalizedSearch)) {
                    paths.push(newPath);
                }

                // For primitive values, check directly
                if (typeof v !== 'object' || v === null) {
                    if (normalizeForSearch(v).includes(normalizedSearch)) {
                        paths.push(newPath);
                    }
                } else {
                    // Only recurse for objects
                    processObject(v, newPath, depth + 1);
                }

                processed++;
            });
            return;
        }

        // Handle Immutable Lists - with optimization for large lists
        if (List.isList(value)) {
            // For large lists, limit processing
            const maxItems = value.size > 100 ? 50 : value.size;

            for (let i = 0; i < maxItems; i++) {
                const item = value.get(i);
                const newPath = path ? `${path}.${i}` : String(i);

                // For primitive values, check directly
                if (typeof item !== 'object' || item === null) {
                    if (normalizeForSearch(item).includes(normalizedSearch)) {
                        paths.push(newPath);
                    }
                } else {
                    // Only recurse for objects
                    processObject(item, newPath, depth + 1);
                }
            }
            return;
        }

        // Handle arrays - with optimization for large arrays
        if (Array.isArray(value)) {
            const maxItems = value.length > 100 ? 50 : value.length;

            for (let i = 0; i < maxItems; i++) {
                const item = value[i];
                const newPath = path ? `${path}.${i}` : String(i);

                // For primitive values, check directly
                if (typeof item !== 'object' || item === null) {
                    if (normalizeForSearch(item).includes(normalizedSearch)) {
                        paths.push(newPath);
                    }
                } else {
                    // Only recurse for objects
                    processObject(item, newPath, depth + 1);
                }
            }
            return;
        }

        // Handle plain objects
        if (typeof value === 'object') {
            const entries = Object.entries(value);
            const maxItems = entries.length > 100 ? 50 : entries.length;

            for (let i = 0; i < maxItems; i++) {
                const [k, v] = entries[i];
                const newPath = path ? `${path}.${k}` : k;

                if (normalizeForSearch(k).includes(normalizedSearch)) {
                    paths.push(newPath);
                }

                // For primitive values, check directly
                if (typeof v !== 'object' || v === null) {
                    if (normalizeForSearch(v).includes(normalizedSearch)) {
                        paths.push(newPath);
                    }
                } else {
                    // Only recurse for objects
                    processObject(v, newPath, depth + 1);
                }
            }
        }
    };

    try {
        processObject(obj, currentPath);
    } catch (err) {
        console.error('Error while finding paths to matching values:', err);
    }

    // Limit the number of paths returned to prevent UI issues
    return paths.slice(0, 100);
};

/**
 * Helper function to check if a section should be included in search results
 * @param {string} key - Section key
 * @param {any} value - Section value
 * @param {string} searchTerm - Search term
 * @returns {boolean} - Whether the section should be included
 */
export const sectionContainsMatch = (
    key: string,
    value: any,
    searchTerm: string,
): boolean => {
    if (!searchTerm) return true; // Show everything when no search

    const normalizedSearch = normalizeForSearch(searchTerm);
    const normalizedKey = normalizeForSearch(key);

    // Check if this is a path search (contains dots)
    if (searchTerm.includes('.')) {
        // If the search starts with this key, it's a match
        const firstSegment = normalizedSearch.split('.')[0];
        if (firstSegment === normalizedKey) return true;
        return false;
    }

    // Check if key matches with normalized comparison
    if (normalizedKey.includes(normalizedSearch)) return true;

    // ID-specific search
    if (
        (normalizedSearch.includes('-') || /^\d+$/.test(normalizedSearch)) &&
        ['chats', 'messages', 'users', 'contacts', 'conversations'].includes(
            normalizedKey,
        )
    ) {
        return true;
    }

    // Check if value contains the search term
    return valueMightContain(value, searchTerm);
};
